import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { TextField, MenuItem, Autocomplete, Box, Chip, Select, FormControl } from "@mui/material";
import { AbsDropDown, Button, Input, Label, TextArea } from "abs-ui-components";

const FormField = ({
    name,
    label,
    type = "input", // input | dropdown | autocomplete | multiselect
    options = [],
    placeholder = "",
    onBlurCallback = () => { },
    setValue,
    rules = {}, // <- custom validation rules passed from parent
    required = false, // for label only
    maxLength,
    rows
    
}) => {
    const {
        control,
        formState: { errors },
    } = useFormContext();



    const getError = errors?.[name];

    return (
        <>
            <Label name={label} bold required={required} />

            <Controller
                name={name}
                control={control}
                rules={rules} // apply custom validation here
                render={({ field }) => {
                    switch (type) {
                        case "input":
                            return (
                                <>
                                    <Input
                                        id={name}
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={() => onBlurCallback(name)}
                                        className="form-input"
                                        autoFocus={false}

                                    />
                                    {getError && <div className="error-msg">{getError.message}</div>}
                                </>
                            );

                        case "dropdown":
                            return (
                                <>
                                    <AbsDropDown
                                        id={name}
                                        options={options}
                                        selected={field.value}
                                        handleChange={(selected) => {
                                            field.onChange(selected.value);
                                        }}
                                        onBlur={() => onBlurCallback(name)}
                                        placeholder={placeholder}
                                    />
                                    {getError && <div className="error-msg">{getError.message}</div>}
                                </>
                            );

                        case "autocomplete":
                            return (
                                <>
                                    <Autocomplete
                                        id={name}
                                        options={options}
                                        getOptionLabel={(option) => option.label}
                                        isOptionEqualToValue={(option, value) => option.value === value}
                                        value={options.find((opt) => opt.value === field.value) || null}
                                        onChange={(_, selected) => field.onChange(selected?.value || "")}
                                        onBlur={() => onBlurCallback(name)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={placeholder}
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    sx: {
                                                        backgroundColor: "#f5f6f9",
                                                        borderRadius: "4px",
                                                        fontSize: "0.875rem",
                                                        height: 38,
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                    {getError && <div className="error-msg">{getError.message}</div>}
                                </>
                            );

                        case "multiselect":
                            return (
                                <>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            multiple
                                            value={field.value || []}
                                            onChange={field.onChange}
                                            displayEmpty
                                            renderValue={(selected) => (
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                    {selected.map((val) => {
                                                        const item = options.find((opt) => opt.code === val);
                                                        return (
                                                            <Chip
                                                                key={val}
                                                                label={item?.name || val}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                onDelete={() => {
                                                                    const newValue = selected.filter((v) => v !== val);
                                                                    field.onChange(newValue);
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        >
                                            {options.length === 0 ? (
                                                <MenuItem disabled sx={{ justifyContent: "center", fontStyle: "italic" }}>
                                                    No options
                                                </MenuItem>
                                            ) : (
                                                options.map((option) => (
                                                    <MenuItem key={option.id} value={option.code}>
                                                        {option.name}
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    </FormControl>
                                    {getError && <div className="error-msg">{getError.message}</div>}
                                </>
                            );

                        case "textarea":
                            return (
                                <>
                                    <TextArea
                                        id={name}
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={() => onBlurCallback(name)}
                                        placeholder={placeholder}
                                        className="form-textarea"
                                        maxLength={maxLength}
                                        rows={rows}
                                    />
                                    {getError && <div className="error-msg">{getError.message}</div>}
                                </>
                            );


                        default:
                            return null;
                    }
                }}
            />
        </>
    );
};

export default FormField;
